/**
 * EggVisual — single source of truth for rendering an egg across all 8 tiers.
 *
 * Themes are driven by the backend `theme` object on each egg:
 *   { base, highlight, shadow, ring, label_color, animated, shimmer,
 *     flame?, metallic?, rainbow?, name }
 *
 * Props:
 *   theme           — egg.theme (backend payload)
 *   letterGrade     — "E" | "D" | "C" | "B" | "A" | "S" | "SS" | "SSS"
 *   size            — number (px) of the visible egg disc. Default 64.
 *   showRank        — render the letter-grade chip badge. Default true.
 *   ready           — adds a gold ring + pulse to signal hatch-ready.
 *   silhouette      — Pokédex-style: renders a pure-black egg with "???"
 *                     and hides every sigil/overlay. Used for tiers the
 *                     user hasn't unlocked yet.
 */
export default function EggVisual({
  theme,
  letterGrade = "E",
  size = 64,
  showRank = true,
  ready = false,
  silhouette = false,
  className = "",
}) {
  const t = theme || {
    base: "#8C8C8C", highlight: "#BFBFBF", shadow: "#3D3D3D",
    ring: "#666666", label_color: "#D6D6D6",
  };

  // Egg shape: a rounded ellipse a bit taller than wide
  const w = size;
  const h = Math.round(size * 1.25);

  // 🥚❓ Pokédex silhouette mode — render a pure black egg with "???"
  // hiding all theme details. Used for tiers the user has never hatched.
  if (silhouette) {
    return (
      <div
        className={`relative inline-block ${className}`}
        style={{ width: w, height: h }}
        data-testid="egg-visual-silhouette"
        aria-label="Unknown egg tier"
      >
        <div
          className="absolute inset-0"
          style={{
            borderRadius: `50% 50% 45% 45% / 60% 60% 40% 40%`,
            border: `2px solid #1F1F1F`,
            background: `radial-gradient(circle at 35% 30%, #1A1A1A 0%, #0A0A0A 55%, #000 100%)`,
            boxShadow: `inset 0 -6px 12px #000, 0 4px 10px rgba(0,0,0,0.5)`,
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 flex items-center justify-center select-none"
          style={{
            color: "#3F3F3F",
            fontSize: Math.round(size * 0.42),
            fontWeight: 900,
            letterSpacing: "0.05em",
            textShadow: "0 0 4px rgba(0,0,0,0.8)",
          }}
        >
          ???
        </div>
        {showRank && (
          <div
            data-testid="egg-rank-locked"
            className="egg-rank-badge absolute -top-2 -right-2 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest"
            style={{
              background: "#0F0F0F",
              color: "#3F3F3F",
              border: "1px solid #1F1F1F",
              minWidth: 22,
              textAlign: "center",
            }}
          >
            ?
          </div>
        )}
      </div>
    );
  }

  // Build effect classes per tier
  const effectClasses = [];
  if (t.rainbow)  effectClasses.push("egg-omni-rainbow");
  else if (t.metallic) effectClasses.push("egg-exotic-metallic");
  else if (t.flame)    effectClasses.push("egg-mythic-flame");
  else if (t.shimmer && letterGrade === "A") effectClasses.push("egg-legendary-glow");

  // Non-rainbow base: a soft radial gradient using theme.base + highlight.
  // 🇺🇸 Tricolor (Memorial Day, July 4th) → red/white/blue horizontal stripes
  // 🎃 Spooky (Halloween) → orange/black vertical gradient w/ purple ring
  const bgStyle = t.rainbow
    ? {}
    : t.tricolor
    ? {
        background: `linear-gradient(180deg,
          ${t.ring || "#DC2626"} 0%,
          ${t.ring || "#DC2626"} 33%,
          ${t.highlight || "#FFFFFF"} 33%,
          ${t.highlight || "#FFFFFF"} 66%,
          ${t.base || "#1E3A8A"} 66%,
          ${t.base || "#1E3A8A"} 100%)`,
        boxShadow: `inset 0 -4px 10px rgba(0,0,0,0.4), 0 4px 12px rgba(0,0,0,0.4), inset 0 0 16px rgba(255,255,255,0.18)`,
      }
    : {
        background: `radial-gradient(circle at 35% 30%, ${t.highlight} 0%, ${t.base} 45%, ${t.shadow} 100%)`,
        boxShadow: `inset 0 -6px 12px ${t.shadow}, 0 4px 10px rgba(0,0,0,0.35)`,
      };

  return (
    <div className={`relative inline-block ${className}`} style={{ width: w, height: h }}>
      {/* The egg shell */}
      <div
        data-testid="egg-visual-shell"
        className={`absolute inset-0 ${effectClasses.join(" ")}`}
        style={{
          borderRadius: `50% 50% 45% 45% / 60% 60% 40% 40%`,
          border: `2px solid ${t.ring}`,
          ...bgStyle,
        }}
      />
      {/* ⭐ Tricolor accent — a single white star on the blue band */}
      {t.tricolor && (
        <div
          aria-hidden
          className="absolute pointer-events-none select-none text-white"
          style={{
            bottom: "12%",
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: Math.round(size * 0.22),
            lineHeight: 1,
            textShadow: "0 0 6px rgba(255,255,255,0.7)",
          }}
        >
          ★
        </div>
      )}
      {/* 👨‍🍳 Chef Hat overlay — signature Chief Cook David Lauer egg */}
      {t.chef_hat && (
        <div
          data-testid="egg-chef-hat"
          aria-hidden
          className="absolute pointer-events-none select-none"
          style={{
            top: `-${Math.round(size * 0.32)}px`,
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: Math.round(size * 0.55),
            lineHeight: 1,
            filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.45))",
          }}
        >
          👨‍🍳
        </div>
      )}
      {/* ✨ Yellow trim band — chef egg flavor pop */}
      {t.chef_hat && (
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            top: "55%",
            left: "8%",
            right: "8%",
            height: "4px",
            background: `linear-gradient(90deg, transparent, ${t.highlight || "#FCD34D"}, transparent)`,
            boxShadow: `0 0 6px ${t.highlight || "#FCD34D"}`,
            borderRadius: "2px",
          }}
        />
      )}
      {/* 🐦‍🔥 Phoenix sigil overlay — Omni Unlimited egg signature */}
      {t.phoenix_sigil && (
        <div
          data-testid="egg-phoenix-sigil"
          aria-hidden
          className="absolute pointer-events-none select-none"
          style={{
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            fontSize: Math.round(size * 0.55),
            lineHeight: 1,
            filter: `drop-shadow(0 0 6px ${t.ring || "#DC2626"}) drop-shadow(0 0 12px ${t.highlight || "#D4AF37"})`,
          }}
        >
          🐦‍🔥
        </div>
      )}
      {/* ✨ Gold trim band — Omni Unlimited signature line */}
      {t.phoenix_sigil && (
        <div
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            top: "62%",
            left: "10%",
            right: "10%",
            height: "3px",
            background: `linear-gradient(90deg, transparent, ${t.highlight || "#D4AF37"}, transparent)`,
            boxShadow: `0 0 8px ${t.highlight || "#D4AF37"}`,
            borderRadius: "2px",
          }}
        />
      )}
      {/* Hatch-ready: pulsing gold ring */}
      {ready && (
        <div
          aria-hidden
          className="absolute inset-[-4px] pointer-events-none animate-pulse"
          style={{
            borderRadius: `50% 50% 45% 45% / 60% 60% 40% 40%`,
            border: `2px solid #D4AF37`,
            boxShadow: `0 0 16px rgba(212, 175, 55, 0.55)`,
          }}
        />
      )}
      {/* Letter-grade rank chip */}
      {showRank && (
        <div
          data-testid={`egg-rank-${letterGrade}`}
          className="egg-rank-badge absolute -top-2 -right-2 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest"
          style={{
            background: t.rainbow ? "linear-gradient(135deg,#FF0080,#FFD700,#00BFFF)" : "#0F0F0F",
            color: t.rainbow ? "#000" : (t.label_color || "#FFFFFF"),
            border: `1px solid ${t.ring}`,
            minWidth: 22,
            textAlign: "center",
          }}
        >
          {letterGrade}
        </div>
      )}
    </div>
  );
}
